import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthModal from "../components/AuthModal";

function Cart({ cart = [], setCart }) {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);

  const handleCheckout = () => {
    const token = localStorage.getItem("token");
    if (!token) { setShowAuth(true); return; }
    navigate("/checkout");
  };

  const increase = (id) =>
    setCart(cart.map(item =>
      item.id === id ? { ...item, quantity: item.quantity + 1 } : item
    ));

  const decrease = (id) =>
    setCart(cart.map(item =>
      item.id === id && item.quantity > 1
        ? { ...item, quantity: item.quantity - 1 }
        : item
    ));

  const remove = (id) => setCart(cart.filter(item => item.id !== id));

  const originalTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const total         = cart.reduce((sum, item) => sum + (item.salePrice ?? item.price) * item.quantity, 0);
  const discount      = originalTotal - total;
  const hasSale       = discount > 0;

  return (
    <>
    <div className="ct-page">
      {/* Atmosphere */}
      <div aria-hidden="true" className="pd2-bg">
        <div className="pd2-orb ct-orb1" />
        <div className="pd2-orb ct-orb2" />
      </div>

      <div className="ct-inner">

        {/* Header */}
        <div className="ct-header">
          <h1 className="ct-title">Your Cart</h1>
          {cart.length > 0 && (
            <span className="ct-count">
              {cart.length} item{cart.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        <div className="ct-body">

          {/* ── Empty state ── */}
          {cart.length === 0 && (
            <div className="ct-empty">
              <svg className="ct-empty-icon" width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 01-8 0"/>
              </svg>
              <p className="ct-empty-title">Cart is empty</p>
              <p className="ct-empty-sub">Add items from the menu to get started</p>
              <button className="ct-continue-btn" style={{ width: "auto", padding: "13px 28px", marginTop: 8 }} onClick={() => navigate("/")}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M19 12H5M12 5l-7 7 7 7"/>
                </svg>
                Browse menu
              </button>
            </div>
          )}

          {/* ── Item list ── */}
          {cart.length > 0 && (
            <div className="ct-items">
              {cart.map((item, idx) => (
                <div
                  key={item.id}
                  className="ct-item"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  {/* Left: image + info */}
                  <div className="ct-item-left">
                    <div className="ct-item-img">
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.name} />
                      ) : (
                        <div className="ct-item-img-empty">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                            <rect x="3" y="3" width="18" height="18" rx="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <path d="M21 15l-5-5L5 21"/>
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="ct-item-info">
                      <div className="ct-item-name">{item.name}</div>
                      <div className="ct-item-unit">
                        {item.salePrice != null ? (
                          <>
                            <span className="ct-price-was">₹{Number(item.price).toLocaleString("en-IN")}</span>
                            {" "}₹{Number(item.salePrice).toLocaleString("en-IN")} each
                            {item.salePercent != null && (
                              <span className="ct-item-sale-badge">{item.salePercent}% OFF</span>
                            )}
                          </>
                        ) : (
                          <>₹{Number(item.price).toLocaleString("en-IN")} each</>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right: stepper + total + remove */}
                  <div className="ct-item-right">
                    <div className="ct-stepper">
                      <button className="ct-step-btn" onClick={() => decrease(item.id)} aria-label="Decrease quantity">−</button>
                      <span className="ct-step-num">{item.quantity}</span>
                      <button className="ct-step-btn" onClick={() => increase(item.id)} aria-label="Increase quantity">+</button>
                    </div>

                    <span className="ct-item-total">
                      ₹{((item.salePrice ?? item.price) * item.quantity).toLocaleString("en-IN")}
                    </span>

                    <button
                      className="ct-remove"
                      onClick={() => remove(item.id)}
                      aria-label={`Remove ${item.name}`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                        <path d="M10 11v6M14 11v6"/>
                        <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Sidebar ── */}
          {cart.length > 0 && (
            <div className="ct-sidebar">

              {/* Order summary */}
              <div className="ct-summary">
                <div className="ct-summary-head">Order Summary</div>

                {hasSale && (
                  <div className="ct-summary-row">
                    <span className="ct-s-label">Original Price</span>
                    <span className="ct-s-val ct-s-val-strike">₹{originalTotal.toLocaleString("en-IN")}</span>
                  </div>
                )}
                {hasSale && (
                  <div className="ct-summary-row">
                    <span className="ct-s-label ct-s-label-discount">Discount Saved</span>
                    <span className="ct-s-val ct-s-val-discount">− ₹{discount.toLocaleString("en-IN")}</span>
                  </div>
                )}
                {hasSale && (
                  <div className="ct-summary-row">
                    <span className="ct-s-label">Subtotal</span>
                    <span className="ct-s-val">₹{total.toLocaleString("en-IN")}</span>
                  </div>
                )}
                <div className="ct-summary-row">
                  <span className="ct-s-label">Shipping</span>
                  <span className="ct-s-val">At checkout</span>
                </div>
                <div className="ct-summary-divider" />
                {hasSale && (
                  <div className="ct-savings-banner">
                    You're saving ₹{discount.toLocaleString("en-IN")} on this order
                  </div>
                )}
                <div className="ct-total-row">
                  <span className="ct-total-label">Total</span>
                  <span className="ct-total-val">₹{total.toLocaleString("en-IN")}</span>
                </div>

                <button className="ct-checkout-btn" onClick={handleCheckout}>
                  Proceed to Checkout
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </button>
                <button className="ct-continue-btn" onClick={() => navigate("/")}>
                  Continue Shopping
                </button>
              </div>

              {/* Trust indicators */}
              <div className="ct-trust">
                <div className="ct-trust-row"><span className="ct-trust-dot" />Free shipping on orders above ₹499</div>
                <div className="ct-trust-row"><span className="ct-trust-dot" />7-day easy returns</div>
                <div className="ct-trust-row"><span className="ct-trust-dot" />Secure checkout</div>
              </div>

            </div>
          )}

        </div>
      </div>
    </div>

    {showAuth && (
      <AuthModal
        initialMode="register"
        onClose={() => setShowAuth(false)}
      />
    )}
    </>
  );
}

export default Cart;
